import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import { TRowItem } from "../../components/Table/TableRow";
import DamsTable from "../../components/Table/CollapsibleTable";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";
import useAppNavigation from "../../hooks/useAppNavigation";

import {
  IDistributionBatchRecord,
  IPagination,
  IQueryPerams,
} from "../../config/types";

import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";

import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import { AssetDistributionLogsFilter } from "../../common-ui/page-filters/AssetDistributionLogsFilter";
import { BadgeLabel } from "../../components/Label";
import appRoutePath from "../../config/path";
import { DistributionBatchStatus } from "../../config/constants/common";
import { useAppSelector } from "../../store/hooks";
import { systemUser } from "../../config/constants/system-users";

const InBoundDistributionPage = () => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const account = useAppSelector((data) => data.authVerify);
  const isSuperAdmin = account.role === systemUser.SUPER_ADMIN;

  const FetchBatchDistributions = async (query: object) => {
    if (isSuperAdmin) {
      return assetBatchDistributionApi.getBatchDistributions(query);
    }
    return assetBatchDistributionApi.getBatchDistributions(query, {
      to_location_id: account.domain_id,
    });
  };

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<IDistributionBatchRecord[]>(FetchBatchDistributions);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const populateDataSet = (
    data: IDistributionBatchRecord[],
    pagination?: IPagination
  ) => {
    const formatData = data.map((data) => ({
      ...data,
      from_location_domain: `${data.from_location_domain}(${data.from_location})`,
      to_location_domain: `${data.to_location_domain}(${data.to_location})`,
    }));

    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    const result = await FetchBatchDistributions({
      is_deleted: false,
    });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };
  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useAppNavigation();

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };

    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={assetBatchDistributionApi.deleteBatchDistribution}
          idKey={"id"}
        />
      </Stack>
    );
  };
  const renderTableLinkActions = ({ item }: { item: TRowItem }) => {
    const record = item as IDistributionBatchRecord;
    const toggleDetailButton = async () => {
      navigate({
        path: appRoutePath.INBOUND_ASSET_DISTRIBUTIONS_DETAILS_PAGE,
        params: { id: record.id },
      });
    };
    const toggleActionButton = async () => {
      navigate({
        path: appRoutePath.INBOUND_ASSET_DISTRIBUTIONS_ACTION_PAGE,
        params: { id: item.id },
      });
    };

    const showActionButton =
      record.distribution_batch_status !== DistributionBatchStatus.pending;
    return (
      <Stack direction={"row"} spacing={1} pl={2}>
        <BadgeLabel
          onClick={toggleDetailButton}
          title={"Details"}
          colorType="blue"
          tip="Go details page"
        />
        {showActionButton && (
          <BadgeLabel
            onClick={toggleActionButton}
            title={"Take Action"}
            colorType="green"
            tip="Accept or Reject assets distributed"
          />
        )}
      </Stack>
    );
  };

  return (
    <MainPageBase
      pageState={"main"}
      title={"Inbound Asset Distributions"}
      subtitle={
        "Review incoming distributions of assets from various locations. You can view details, take actions, or export records."
      }
      exportBtnOnClick={() => {
        alert("Exporting...");
      }}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        justifyContent={"center"}
      >
        <Stack width={"100%"}>
          <AssetDistributionLogsFilter
            ftn={FetchBatchDistributions}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
            isDeleted={false}
          />
          <DamsTable
            tableTitle="Inbound Distribution Records"
            tableSubtitle="Displaying all received asset batches"
            numTableRecords="25"
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            maxRowCells={6}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            selectedRowItems={selectedRowItems}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            headerDropDownMenu={renderHeaderDropDownMenu}
            extraRowComponent={renderTableLinkActions}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
};

const tkeys = [
  "from_location_domain",
  "to_location_domain",
  "quantity",
  "distribution_batch_status",
];

const columns = [
  "Distributor Domain",
  "Recipent Domain",
  "Number of Assets",
  "Status",
];
const hkeys = ["from_location_name", "to_location_name"];

const hcolumns = ["Departure Location", "Destination"];

export default InBoundDistributionPage;
